import React, { useState, useEffect } from 'react';
import { useAppTheme } from '../../theme/AppThemeContext.jsx';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Paper,
  Divider,
  Checkbox,
  ButtonBase,
  FormHelperText,
  Dialog,
} from '@mui/material';
import {
  Close as CloseIcon,
  CalendarToday as CalendarTodayOutlined,
  AccessTime as AccessTimeOutlined,
  Loop as LoopIcon,
  Notes as NotesOutlined,
  Event as EventIcon,
  ArrowDropDown as KeyboardArrowDownRoundedIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';

import { ReusableDatePicker } from './ReusableDatePicker.jsx';
import { ReusableTimePickerPopover } from './ReusableTimePicker.jsx';
import { RepeatPickerPopover } from './RepeatPicker.jsx';
import { RecurringTaskUpdateDialog } from './RecurringTaskUpdateDialog.jsx';
import { RecurringTaskDeleteDialog } from './RecurringTaskDeleteDialog.jsx';


const TASK_COLORS = [
  { name: 'Indigo', value: '#6366f1', preview: '#6366f1' },
  { name: 'Teal', value: '#14b8a6', preview: '#14b8a6' },
  { name: 'Emerald', value: '#10b981', preview: '#10b981' },
  { name: 'Rose', value: '#f43f5e', preview: '#f43f5e' },
  { name: 'Purple', value: '#a855f7', preview: '#a855f7' },
  { name: 'Amber', value: '#f59e0b', preview: '#f59e0b' },
];

// Helper function to get next nearest 30 minutes
function getNextNearest30Minutes() {
  const now = new Date();
  const minutes = now.getMinutes();
  
  // Round up to next 30 minutes
  const roundedMinutes = minutes <= 30 ? 30 : 60;
  const hours = now.getHours() + (roundedMinutes === 60 ? 1 : 0);
  const adjustedHours = hours % 24;
  const adjustedMinutes = roundedMinutes === 60 ? 0 : roundedMinutes;
  
  // Format as HH:MM
  const startHours = String(adjustedHours).padStart(2, '0');
  const startMinutes = String(adjustedMinutes).padStart(2, '0');
  const startTime = `${startHours}:${startMinutes}`;
  
  // End time is 30 minutes after start time
  const endHours = adjustedHours + Math.floor((adjustedMinutes + 30) / 60);
  const endMinutes = (adjustedMinutes + 30) % 60;
  const endHoursFormatted = String(endHours % 24).padStart(2, '0');
  const endMinutesFormatted = String(endMinutes).padStart(2, '0');
  const endTime = `${endHoursFormatted}:${endMinutesFormatted}`;
  
  return { startTime, endTime };
}

function TaskDialog({
  open,
  onClose,
  onAddTask,
  onUpdateTask,
  onDeleteTask,
  taskToEdit,
  user,
  existingTasks,
  recurringUpdateDialog,
  handleRecurringUpdateChoice,
  closeRecurringUpdateDialog,
  recurringDeleteDialog,
  handleRecurringDeleteChoice,
  closeRecurringDeleteDialog,
}) {
  const { tokens } = useAppTheme();
  const primaryRgb = tokens.primary.slice(4, -1);
  const hero = tokens.primaryLight;
  const bg = 'rgb(245, 245, 248)';
  const textDark = 'rgb(20, 20, 20)';
  const muted = 'rgb(133, 133, 133)';
  const border = '1px solid rgba(0,0,0,0.08)';

  // Determine if we're in edit mode
  const isEditMode = Boolean(taskToEdit);

  // State
  const defaultTimes = getNextNearest30Minutes();
  const initialTaskData = {
    title: '',
    date: new Date(),
    allDay: false,
    startTime: defaultTimes.startTime,
    endTime: defaultTimes.endTime,
    color: '#6366f1',
    repeat: 'none',
    hasSubtask: false,
    notes: '',
    subtasks: [],
  };

  const [taskData, setTaskData] = useState(initialTaskData);
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);
  const [datePickerAnchor, setDatePickerAnchor] = useState(null);
  const [timePickerAnchor, setTimePickerAnchor] = useState(null);
  const [repeatPickerAnchor, setRepeatPickerAnchor] = useState(null);
  const [newSubtaskText, setNewSubtaskText] = useState('');

  useEffect(() => {
    if (!open) return;
    
    if (isEditMode && taskToEdit) {
      const existingRepeat = taskToEdit.repeat || 'none';
      
      let repeatConfig;
      
      if (existingRepeat === 'none' || typeof existingRepeat === 'string') {
        // Convert string repeat to object format for the form
        repeatConfig = {
          frequency: existingRepeat === 'none' ? 'once' : existingRepeat,
          interval: 1,
          byWeekday: [],
          startDate: taskToEdit.date ? new Date(taskToEdit.date) : new Date(),
          endDate: taskToEdit.seriesEndDate ? taskToEdit.seriesEndDate.toDate() : null,
        };
      } else {
        // Already in object format
        repeatConfig = {
          ...existingRepeat,
          startDate: existingRepeat.startDate ? existingRepeat.startDate.toDate() : (taskToEdit.date ? new Date(taskToEdit.date) : new Date()),
          endDate: existingRepeat.endDate ? existingRepeat.endDate.toDate() : null,
        };
      }
      
      setRepeatValue(repeatConfig);
      
      // Update timeValue with actual task times
      const taskStartTime = taskToEdit.startTime || defaultTimes.startTime;
      const taskEndTime = taskToEdit.endTime || defaultTimes.endTime;
      
      setTimeValue({
        start: taskStartTime,
        end: taskEndTime,
        timeZone: 'Australia/Brisbane',
      });
      
      // Populate form with existing task data
      const taskData = {
        title: taskToEdit.title || '',
        date: taskToEdit.date ? new Date(taskToEdit.date) : new Date(),
        allDay: taskToEdit.allDay || false,
        startTime: taskToEdit.startTime || defaultTimes.startTime,
        endTime: taskToEdit.endTime || defaultTimes.endTime,
        color: taskToEdit.color || '#6366f1',
        repeat: taskToEdit.repeat || 'none',
        hasSubtask: taskToEdit.hasSubtask || false,
        notes: taskToEdit.notes || '',
        subtasks: taskToEdit.subtasks || [],
      };
      
      setTaskData(taskData);
    } else {
      // Reset form for new task
      setTaskData(initialTaskData);
      setRepeatValue({
        frequency: 'once',
        interval: 1,
        byWeekday: [],
        startDate: null,
        endDate: null,
      });
      
      // Reset timeValue to default times for new tasks
      setTimeValue({
        start: defaultTimes.startTime,
        end: defaultTimes.endTime,
        timeZone: 'Australia/Brisbane',
      });
    }
    
    // Reset loading states
    setCreating(false);
    setDatePickerAnchor(null);
    setTimePickerAnchor(null);
    setRepeatPickerAnchor(null);
    setNewSubtaskText('');
  }, [open, isEditMode, taskToEdit]);

  // Time picker state
  const [timeValue, setTimeValue] = useState({
    start: defaultTimes.startTime,
    end: defaultTimes.endTime,
    timeZone: 'Australia/Brisbane',
  });

  // Repeat picker state
  const [repeatValue, setRepeatValue] = useState({
    frequency: 'once',
    interval: 1,
    byWeekday: [],
    startDate: null,
    endDate: null,
  });

  // Helper functions
function formatDateLabel(date) {
  const options = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
  return date.toLocaleDateString('en-US', options);
}

  function calculateDuration(startTime, endTime) {
    if (!startTime || !endTime) return '';
    
    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    const diff = end - start;
    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;
    
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  }

  function parseTimeMinutes(timeStr) {
    const [h, m] = timeStr.split(':').map(Number);
    return h * 60 + m;
  }

  function validate() {
    const newErrors = {};

    if (!taskData.title.trim()) {
      newErrors.title = 'Task name is required.';
    }

    if (!taskData.allDay) {
      if (!taskData.startTime || !taskData.endTime) {
        newErrors.time = 'Start and end time are required.';
      } else if (taskData.startTime >= taskData.endTime) {
        newErrors.time = 'End time must be after start time.';
      } else if (existingTasks && existingTasks.length > 0) {
        const newStart = parseTimeMinutes(taskData.startTime);
        const newEnd = parseTimeMinutes(taskData.endTime);
        const dateStr = taskData.date instanceof Date
          ? taskData.date.toISOString().split('T')[0]
          : taskData.date;
        const conflicts = existingTasks.filter(t => {
          if (t.id === taskToEdit?.id) return false;
          if (t.allDay || !t.startTime || !t.endTime) return false;
          if (t.dateString !== dateStr) return false;
          const tStart = parseTimeMinutes(t.startTime);
          const tEnd = parseTimeMinutes(t.endTime);
          return newStart < tEnd && tStart < newEnd;
        });
        if (conflicts.length > 0) {
          newErrors.overlap = `Overlaps with: ${conflicts.map(c => c.title).join(', ')}`;
        }
      }
    }

    setErrors(newErrors);
    // overlap is a warning only — does not block save
    const blockingErrors = { ...newErrors };
    delete blockingErrors.overlap;
    return Object.keys(blockingErrors).length === 0;
  }

  async function handleSubmit() {
    if (!validate()) return;
    
    setCreating(true);
    try {
      if (isEditMode) {
        // Update existing task
        const updateData = {
          title: taskData.title.trim(),
          date: taskData.date,
          dateString: taskData.date.toISOString().split('T')[0], // Ensure dateString is maintained
          allDay: taskData.allDay,
          startTime: taskData.allDay ? null : taskData.startTime,
          endTime: taskData.allDay ? null : taskData.endTime,
          color: taskData.color,
          repeat: taskData.repeat,
          hasSubtask: taskData.hasSubtask,
          notes: taskData.notes,
        };
        await onUpdateTask(taskToEdit.id, updateData);
      } else {
        // Create new task
        await onAddTask({
          title: taskData.title.trim(),
          date: taskData.date,
          allDay: taskData.allDay,
          startTime: taskData.allDay ? null : taskData.startTime,
          endTime: taskData.allDay ? null : taskData.endTime,
          color: taskData.color,
          repeat: taskData.repeat,
          hasSubtask: taskData.hasSubtask,
          notes: taskData.notes,
        });
      }
      onClose();
    } catch (error) {
      console.error('Error creating/updating task:', error);
    } finally {
      setCreating(false);
    }
  }

  const handleDelete = async () => {
    if (!isEditMode || !taskToEdit) return;
    
    try {
      await onDeleteTask(taskToEdit.id);
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  }

  const handleFieldChange = (field, value) => {
    setTaskData(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDateClick = (event) => {
    setDatePickerAnchor(event.currentTarget);
  };

  const handleDateClose = () => {
    setDatePickerAnchor(null);
  };

  const handleDateChange = (newDate) => {
    handleFieldChange('date', newDate);
  };

  const handleTimeClick = (event) => {
    setTimePickerAnchor(event.currentTarget);
  };

  const handleTimeClose = () => {
    setTimePickerAnchor(null);
  };

  const handleTimeChange = (next) => {
    setTimeValue({
      start: next.start,
      end: next.end,
      timeZone: next.timeZone,
    });
    handleFieldChange('startTime', next.start);
    handleFieldChange('endTime', next.end);
  };

  const handleRepeatClick = (event) => {
    setRepeatPickerAnchor(event.currentTarget);
  };

  const handleRepeatClose = () => {
    setRepeatPickerAnchor(null);
  };

  const handleRepeatChange = (next) => {
    setRepeatValue(next);
    // Store the full repeat configuration, not just the frequency
    const repeatConfig = next.frequency === 'once' ? 'none' : next;
    handleFieldChange('repeat', repeatConfig);
  };

  // Helper function to safely format frequency display
  const formatFrequencyDisplay = (frequency) => {
    if (!frequency || frequency === 'once') return '';
    try {
      return frequency.charAt(0).toUpperCase() + (frequency.slice(1) || '');
    } catch (error) {
      return '';
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth={false}
        sx={{
          '& .MuiDialog-container': {
            justifyContent: { xs: 'center', sm: 'flex-end' },
            alignItems: { xs: 'flex-end', sm: 'flex-start' },
            pt: { xs: 0, sm: 2 },
            pr: { xs: 0, sm: 2 },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: { xs: '16px 16px 0 0', sm: '16px' },
            width: { xs: '100%', sm: 420 },
            maxWidth: '100%',
            height: { xs: '90vh', sm: 'calc(100vh - 32px)' },
            maxHeight: { xs: '90vh', sm: 'calc(100vh - 32px)' },
            m: 0,
            background: bg,
          },
        }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* HERO HEADER */}
          <Box
            sx={{
            position: 'relative',
            background: hero,
            color: 'white',
            px: 3,
            pt: 2.25,
            pb: 2.5,
            overflow: 'hidden',
          }}
        >
          {/* Close */}
          <IconButton
            onClick={onClose}
            aria-label="Close"
            sx={{
              position: 'absolute',
              top: 14,
              right: 14,
              width: 34,
              height: 34,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.85)',
              '&:hover': { background: 'rgba(255,255,255,0.92)' },
            }}
          >
            <CloseIcon sx={{ color: 'rgba(0,0,0,0.6)' }} fontSize="small" />
          </IconButton>

          {/* Timeline rail + icon pill */}
          <Box
            sx={{
              position: 'absolute',
              left: 16,
              top: 14,
              bottom: 14,
              width: 72,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: 56,
                height: '100%',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              {/* Vertical rail */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 10,
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.35)',
                }}
              />
              {/* Icon pill */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 58,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 46,
                  height: 92,
                  borderRadius: 999,
                  background: 'rgba(245,245,248,0.95)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0px 10px 22px rgba(0,0,0,0.15)',
                }}
              >
                <Box
                  component="span"
                  className="material-symbols-rounded"
                  sx={{ fontSize: 22, color: tokens.primary }}
                  aria-hidden="true"
                >
                  checklist
                </Box>
              </Box>

              {/* Decorative dot */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 160,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 26,
                  height: 26,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.35)',
                }}
              />
            </Box>
          </Box>

          {/* Main header content */}
          <Box sx={{ pl: 10, pr: 5 }}>
            <Typography sx={{ fontSize: 12, opacity: 0.9, fontWeight: 400 }}>
              {!taskData.allDay ? 
                `${taskData.startTime} - ${taskData.endTime} (${calculateDuration(taskData.startTime, taskData.endTime)})` : 
                'All-day'
              }
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 42px', alignItems: 'center', mt: 0.5 }}>
              <TextField
                value={taskData.title}
                onChange={(e) => handleFieldChange('title', e.target.value)}
                placeholder="New Task"
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: 26,
                    fontWeight: 400,
                    color: 'white',
                    '&::placeholder': { color: 'rgba(255,255,255,0.85)' },
                  },
                }}
                sx={{
                  input: { padding: 0 },
                }}
                error={!!errors.title}
              />
              
              {/* Radio for visual consistency */}
              <Box sx={{ justifySelf: 'end' }} />
            </Box>
            
            {/* Error message for title */}
            {errors.title && (
              <Box sx={{ mt: 1 }}>
                <FormHelperText error sx={{ m: 0 }}>
                  {errors.title}
                </FormHelperText>
              </Box>
            )}

            {/* Divider */}
            <Box sx={{ mt: 1.25, height: 1, background: 'rgba(255,255,255,0.35)' }} />
          </Box>
        </Box>

        {/* BODY */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.25 }}>
          {/* Date + Time card */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 3,
              border,
              background: 'white',
              overflow: 'hidden',
            }}
          >
            {/* Date row */}
            <ButtonBase
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                width: '100%',
                borderRadius: 1,
                '&:hover': { background: 'rgba(0,0,0,0.02)' },
              }}
              onClick={handleDateClick}
            >
              <EventIcon sx={{ color: tokens.primary }} fontSize="small" />
              <Typography sx={{ fontSize: 14, fontWeight: 400, color: textDark }}>
                {formatDateLabel(taskData.date)}
              </Typography>
              <Box sx={{ ml: 'auto', color: muted }}>
                <CalendarTodayOutlined sx={{ fontSize: 16 }} />
              </Box>
            </ButtonBase>

            {/* Reusable Date Picker */}
            <ReusableDatePicker
              value={taskData.date}
              onChange={handleDateChange}
              open={Boolean(datePickerAnchor)}
              anchorEl={datePickerAnchor}
              onClose={handleDateClose}
              title="Select Date"
            />

            <Divider sx={{ borderColor: 'rgba(0,0,0,0.06)' }} />

            {/* Time row + All-day toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.25 }}>
              <ButtonBase
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  px: 2,
                  py: 1.25,
                  width: '100%',
                  borderRadius: 1,
                  '&:hover': { background: 'rgba(0,0,0,0.02)' },
                }}
                onClick={handleTimeClick}
                disabled={taskData.allDay}
              >
                <AccessTimeOutlined sx={{ color: tokens.primary }} fontSize="small" />
                <Typography sx={{ fontSize: 14, fontWeight: 400, color: textDark }}>
                  {taskData.allDay ? 'All-day' : `${timeValue.start} - ${timeValue.end}`}
                </Typography>
                <Box sx={{ ml: 'auto', color: muted }}>
                  <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />
                </Box>
              </ButtonBase>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox
                  checked={taskData.allDay}
                  onChange={(e) => handleFieldChange('allDay', e.target.checked)}
                  sx={{
                    p: 0.25,
                    color: 'rgba(0,0,0,0.25)',
                    '&.Mui-checked': { color: tokens.primary },
                  }}
                />
                <Typography sx={{ fontSize: 13, fontWeight: 400, color: textDark, whiteSpace: 'nowrap' }}>
                  All-Day
                </Typography>
              </Box>
            </Box>

            {/* Reusable Time Picker */}
            <ReusableTimePickerPopover
              value={timeValue}
              onChange={handleTimeChange}
              open={Boolean(timePickerAnchor)}
              anchorEl={timePickerAnchor}
              onClose={handleTimeClose}
              user={user}
            />

            {/* Overlap warning */}
            {errors.overlap && (
              <Box sx={{ px: 2, pb: 1 }}>
                <FormHelperText
                  sx={{
                    m: 0,
                    color: '#b45309',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    fontSize: 12,
                  }}
                >
                  ⚠ {errors.overlap}
                </FormHelperText>
              </Box>
            )}
          </Paper>

          {/* Repeat pill */}
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleRepeatClick}
              startIcon={<LoopIcon />}
              sx={{
                textTransform: 'none',
                borderRadius: 999,
                px: 3.25,
                py: 1.25,
                fontWeight: 400,
                color: textDark,
                borderColor: 'rgba(0,0,0,0.12)',
                '&:hover': {
                  borderColor: tokens.primary,
                  backgroundColor: `rgba(${primaryRgb}, 0.04)`,
                }
              }}
            >
              Repeat{repeatValue?.frequency && repeatValue.frequency !== 'once' ? ` • ${formatFrequencyDisplay(repeatValue.frequency)}` : ''}
            </Button>

            {/* Repeat Picker */}
            <RepeatPickerPopover
              value={repeatValue}
              onChange={handleRepeatChange}
              open={Boolean(repeatPickerAnchor)}
              anchorEl={repeatPickerAnchor}
              onClose={handleRepeatClose}
              DatePickerComponent={ReusableDatePicker}
            />
          </Box>

          {/* Subtasks card */}
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border,
              background: 'white',
              overflow: 'hidden',
            }}
          >
            {/* Section label */}
            <Box sx={{ px: 2, pt: 1.25, pb: 0.5 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, color: muted, textTransform: 'uppercase' }}>
                Subtasks
              </Typography>
            </Box>

            {/* Existing subtasks */}
            {taskData.subtasks.map((st, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 2,
                  py: 0.75,
                  '&:hover .subtask-remove': { opacity: 1 },
                }}
              >
                {/* Toggle done */}
                <Box
                  component="button"
                  onClick={() => {
                    const updated = taskData.subtasks.map((s, i) =>
                      i === idx ? { ...s, completed: !s.completed } : s
                    );
                    handleFieldChange('subtasks', updated);
                  }}
                  sx={{
                    flexShrink: 0,
                    width: 18,
                    height: 18,
                    borderRadius: '50%',
                    border: `2px solid ${st.completed ? tokens.primary : 'rgba(0,0,0,0.22)'}`,
                    backgroundColor: st.completed ? tokens.primary : 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    padding: 0,
                    transition: 'all 0.18s ease',
                    '&:hover': {
                      borderColor: tokens.primary,
                    },
                  }}
                >
                  {st.completed && (
                    <svg width={10} height={10} viewBox="0 0 24 24" fill="none">
                      <path d="M20 6L9 17l-5-5" stroke="white" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </Box>

                {/* Title */}
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: 14,
                    color: st.completed ? muted : textDark,
                    textDecoration: st.completed ? 'line-through' : 'none',
                  }}
                >
                  {st.title}
                </Typography>

                {/* Remove */}
                <IconButton
                  className="subtask-remove"
                  size="small"
                  onClick={() => {
                    const updated = taskData.subtasks.filter((_, i) => i !== idx);
                    handleFieldChange('subtasks', updated);
                    handleFieldChange('hasSubtask', updated.length > 0);
                  }}
                  sx={{
                    p: 0.25,
                    opacity: 0,
                    transition: 'opacity 0.15s',
                    color: 'rgba(0,0,0,0.28)',
                    '&:hover': { color: 'rgba(239,68,68,0.75)' },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            ))}

            {/* Add subtask input row */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 0.75, pb: 1.25 }}>
              <Box
                sx={{
                  flexShrink: 0,
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  border: '2px dashed rgba(0,0,0,0.18)',
                }}
              />
              <TextField
                value={newSubtaskText}
                onChange={(e) => setNewSubtaskText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newSubtaskText.trim()) {
                    e.preventDefault();
                    const updated = [
                      ...taskData.subtasks,
                      { id: Date.now(), title: newSubtaskText.trim(), completed: false },
                    ];
                    handleFieldChange('subtasks', updated);
                    handleFieldChange('hasSubtask', true);
                    setNewSubtaskText('');
                  }
                }}
                placeholder="Add a subtask…"
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: { fontSize: 14, color: textDark },
                }}
                sx={{ flex: 1 }}
              />
              {newSubtaskText.trim() && (
                <IconButton
                  size="small"
                  onClick={() => {
                    const updated = [
                      ...taskData.subtasks,
                      { id: Date.now(), title: newSubtaskText.trim(), completed: false },
                    ];
                    handleFieldChange('subtasks', updated);
                    handleFieldChange('hasSubtask', true);
                    setNewSubtaskText('');
                  }}
                  sx={{ p: 0.25, color: tokens.primary }}
                >
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={1.8} />
                    <path d="M12 8v8M8 12h8" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" />
                  </svg>
                </IconButton>
              )}
            </Box>
          </Paper>

          {/* Notes card */}
          <Paper
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border,
              background: 'white',
              overflow: 'hidden',
            }}
          >
            {/* Notes */}
            <Box sx={{ px: 2, py: 1.25 }}>
              <TextField
                value={taskData.notes}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                placeholder="Add notes, meeting links or phone numbers..."
                multiline
                minRows={3}
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  sx: {
                    fontSize: 14,
                    color: textDark,
                  },
                }}
                sx={{
                  width: '100%',
                }}
              />
            </Box>
          </Paper>
        </Box>

        {/* FOOTER */}
        <Box
          sx={{
            px: 2.5,
            pb: 2.5,
            pt: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            background: 'transparent',
          }}
        >
          {/* Delete button (only in edit mode) */}
          {isEditMode && (
            <IconButton
              onClick={handleDelete}
              sx={{
                color: 'rgba(239, 68, 68, 0.8)',
                '&:hover': {
                  color: 'rgba(239, 68, 68, 1)',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                },
              }}
            >
              <DeleteIcon />
            </IconButton>
          )}

          {/* Create/Update button */}
          <Button
            onClick={handleSubmit}
            disabled={creating}
            sx={{
              textTransform: 'none',
              borderRadius: 999,
              px: 3.25,
              py: 1.25,
              fontWeight: 400,
              color: 'white',
              background: tokens.primary,
              boxShadow: '0px 10px 22px rgba(0,0,0,0.10)',
              '&:hover': { background: tokens.primary },
              opacity: creating ? 0.75 : 1,
            }}
          >
            {creating ? (isEditMode ? 'Updating...' : 'Creating...') : (isEditMode ? 'Update Task' : 'Create Task')}
          </Button>
        </Box>
      </Box>
    </Dialog>
    
    {/* Recurring Task Update Dialog */}
    <RecurringTaskUpdateDialog
      open={recurringUpdateDialog?.open || false}
      onClose={closeRecurringUpdateDialog}
      onUpdateChoice={handleRecurringUpdateChoice}
    />
    
    {/* Recurring Task Delete Dialog */}
    <RecurringTaskDeleteDialog
      open={recurringDeleteDialog?.open || false}
      onClose={closeRecurringDeleteDialog}
      onDeleteChoice={handleRecurringDeleteChoice}
    />
    </>
  );
}

export default TaskDialog;
